import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuContent, NavigationMenuTrigger, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="21103" className={cn("flex gap-4", isMobile ? "flex-col items-center" : "items-center")}>
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-20 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col items-center gap-6">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-medium text-foreground hover:text-ring transition-colors" onClick={closeMobileMenu} data-index={i}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-full bg-transparent px-4 py-2 text-sm font-medium tracking-tight transition-colors hover:bg-accent/80 hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 h-[80px] flex items-center", isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/20" : "bg-background border-b border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between h-full">
        {}
        <div className="flex-shrink-0 w-[140px]">
          <Link to="/HomePage" className="flex items-center gap-2">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605423/img/logo_the_coffi_code.png" alt="Lana Connect LLC Logo" className="h-8 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <div className="bg-background/40 backdrop-blur-md border border-border/30 rounded-full px-2 py-1 shadow-sm">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>
        </div>

        {}
        <div className="hidden md:flex items-center justify-end w-[140px] gap-4">
          <Button asChild className="relative overflow-hidden bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md hover:shadow-lg border-2 border-transparent hover:border-ring/20 rounded-full">
            <Link to="/OurSolutionsPage">
              <span className="relative z-10 font-semibold tracking-tight">Get Started</span>
              <span className="absolute inset-0 rounded-full ring-2 ring-ring/30 animate-pulse" />
            </Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-foreground hover:bg-accent/40">
            <Menu className="h-6 w-6" />
            <span className="sr-only">Open menu</span>
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-2xl md:hidden flex flex-col animate-in fade-in duration-300">
          <div className="container mx-auto px-4 h-[80px] flex items-center justify-between border-b border-border/10">
            <Link to="/HomePage" onClick={() => setIsMobileMenuOpen(false)}>
               <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605423/img/logo_the_coffi_code.png" alt="Lana Connect LLC Logo" className="h-8 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:bg-accent/40">
              <X className="h-6 w-6" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center gap-10 p-8">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="mt-8 w-full max-w-xs">
              <Button asChild className="w-full h-14 text-xl font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl rounded-full">
                <Link to="/OurSolutionsPage" onClick={() => setIsMobileMenuOpen(false)}>
                  Get Started
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}